import { showError, showSuccess } from '@calendly/alert';
import { useQueryParams } from '@calendly/hooks';
import { HtmlParser } from '@calendly/html-parser';
import { I18n } from '@calendly/i18n';
import { Table, Header, HeaderCell, Body, Row, Cell } from '@calendly/table';
import { ArrowRoundLeftIcon, InformationCircleMiniIcon } from '@calendly/ui/components/icons';
import { Link } from '@calendly/ui/components/link';
import { MinimalButton } from '@calendly/ui/components/minimal-button';
import { Pagination } from '@calendly/ui/components/pagination';
import { Tooltip } from '@calendly/ui/components/tooltip';
import { BodyText } from '@calendly/ui/components/typography';
import { useDeviceType } from '@calendly/ui/hooks/use-device-type';
import classNames from 'classnames';
import moment from 'moment-timezone';
import { useState, useEffect, forwardRef } from 'react';
import { useDispatch } from 'react-redux';
import { setGetParams } from 'actions/core/navigation';
import { useGetUserQuery } from 'common';
import LoadingState from 'components/core/loading_state';
import { CONTENT_PAGES } from 'features/empty_state';
import { useFetchBlockedSourcesQuery, useUnblockSourceMutation } from './api';
import { UnblockConfirmationModal } from './unblock_modal';
import { EMPTY_STATE_TYPES, EmptyState } from '../../../components/empty_state';
const PAGE_SIZE = 20;
const descriptionIsTabletDownCss = "d1j3xnal";
const descriptionCss = "d1672qkf";
const Description = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  const isTabletDown = useDeviceType('isTabletDown');
  return <Component ref={ref} {...restProps} className={classNames(descriptionCss, {
    [descriptionIsTabletDownCss]: isTabletDown
  }, props.className)} />;
});
const HelpCenterLink = ({
  children
}) => <Link as="a" href="https://calendly.com/help/how-to-block-someone-from-booking-with-you" target="_blank" rel="noopener noreferrer">
    {children}
  </Link>;
const BlockedPeopleSettingsTableBody = ({
  blockedPeopleData,
  handleBlockAction
}) => <Body>
    {blockedPeopleData.map(blockedData => <Row key={blockedData.id}>
        <Cell label={I18n.t('publisher.organization_settings.blocked_sources.host_block.name')}>
          <BodyText weight="bold" inline>
            {blockedData.inviteeName}
          </BodyText>
        </Cell>
        <Cell label={I18n.t('publisher.organization_settings.blocked_sources.host_block.associated_email')}>
          {blockedData.inviteeEmail}
        </Cell>
        <Cell label={I18n.t('publisher.organization_settings.blocked_sources.host_block.reported_meeting')}>
          {blockedData.meetingName}
        </Cell>
        <Cell label={I18n.t('publisher.organization_settings.blocked_sources.host_block.block_date')}>
          {moment(blockedData.createdAt).format('ll')}
        </Cell>
        <Cell hasActions>
          <MinimalButton onClick={() => handleBlockAction(blockedData)}>
            <ArrowRoundLeftIcon />
            {I18n.t('publisher.organization_settings.blocked_sources.host_block.unblock')}
          </MinimalButton>
        </Cell>
      </Row>)}
  </Body>;
const headerWithTooltipCss = "h12w2012";
const HeaderWithTooltip = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'span';
  return <Component ref={ref} {...restProps} className={classNames(headerWithTooltipCss, props.className)} />;
});
const tooltipWrapperCss = "t1e3967d";
const TooltipWrapper = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'span';
  return <Component ref={ref} {...restProps} className={classNames(tooltipWrapperCss, props.className)} />;
});
const paginationContainerCss = "p2snxmq";
const PaginationContainer = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  return <Component ref={ref} {...restProps} className={classNames(paginationContainerCss, props.className)} />;
});
export const BlockedSourcesSettings = () => {
  const dispatch = useDispatch();
  const {
    page: initialPage,
    sortField: initSortField,
    order: initSortOrder
  } = useQueryParams();
  const [selectedBlockedSource, setSelectedBlockedSource] = useState(null);
  const [page, setPage] = useState(() => {
    if (initialPage) {
      const parsed = parseInt(initialPage, 10);
      return Number.isNaN(parsed) || parsed < 1 ? 1 : parsed;
    }
    return 1;
  });
  const [sortField, setSortField] = useState(initSortField || 'created_at');
  const [sortOrder, setSortOrder] = useState(initSortOrder || 'desc');
  const [isConfirming, setIsConfirming] = useState(false);
  const {
    data: {
      id: userId
    } = {},
    isLoading: isUserLoading
  } = useGetUserQuery();
  const {
    data,
    isLoading
  } = useFetchBlockedSourcesQuery({
    userId,
    sortBy: sortField,
    sortOrder,
    page,
    perPage: PAGE_SIZE
  }, {
    refetchOnMountOrArgChange: true,
    skip: !userId
  });
  const [unblockSource] = useUnblockSourceMutation();
  const paginationData = data?.pagination ?? {};
  const {
    totalCount = 0
  } = paginationData;
  const totalPages = totalCount > 0 ? Math.ceil(totalCount / PAGE_SIZE) : 0;
  const sortedColumn = {
    name: sortField,
    direction: sortOrder
  };
  useEffect(() => {
    if (data && !isLoading && data.results?.length === 0 && totalCount > 0 && page > 1) {
      setPage(1);
      dispatch(setGetParams({
        page: 1
      }));
    }
  }, [data, isLoading, page, totalCount, dispatch]);
  const handleUnblockModal = blockedData => {
    setSelectedBlockedSource(blockedData);
    setIsConfirming(true);
  };
  const handleClose = () => {
    setIsConfirming(false);
    setSelectedBlockedSource(null);
  };
  const handleConfirm = () => {
    if (!userId || !selectedBlockedSource?.uuid) {
      dispatch(showError(I18n.t('publisher.organization_settings.blocked_sources.unblock_error')));
      return;
    }
    unblockSource({
      userId,
      id: selectedBlockedSource.uuid,
      location: 'settings_page'
    }).unwrap().then(() => {
      dispatch(showSuccess(I18n.t('publisher.organization_settings.blocked_sources.unblock_success')));
      handleClose();
    }).catch(() => {
      dispatch(showError(I18n.t('publisher.organization_settings.blocked_sources.unblock_error')));
    });
  };
  const sortEventTypes = column => {
    let newSortOrder = 'asc';
    if (sortField === column) {
      newSortOrder = sortOrder === 'asc' ? 'desc' : 'asc';
    }
    setSortOrder(newSortOrder);
    setSortField(column);
    dispatch(setGetParams({
      sortField: column,
      order: newSortOrder
    }));
  };
  const updatePage = nextPage => {
    if (nextPage >= 1 && nextPage <= totalPages) {
      setPage(nextPage);
      dispatch(setGetParams({
        page: nextPage
      }));
    } else {
      setPage(1);
      dispatch(setGetParams({
        page: 1
      }));
    }
  };
  if (isUserLoading || isLoading) {
    return <LoadingState />;
  }
  if (totalCount === 0) {
    return <EmptyState type={EMPTY_STATE_TYPES.centered} contentKey={CONTENT_PAGES.blockedSources} options={{
      isButtonHidden: true
    }} />;
  }
  return <>
      <Description>
        {HtmlParser(I18n.t('publisher.organization_settings.blocked_sources.description'), {
        HelpCenterLink
      })}
      </Description>
      <Table sortedColumn={sortedColumn} onSort={sortEventTypes}>
        <Header>
          <HeaderCell columnName="invitee_name">
            {I18n.t('publisher.organization_settings.blocked_sources.host_block.name')}
          </HeaderCell>
          <HeaderCell columnName="invitee_email">
            <HeaderWithTooltip>
              {I18n.t('publisher.organization_settings.blocked_sources.host_block.associated_email')}
              <TooltipWrapper>
                <Tooltip text={I18n.t('publisher.organization_settings.blocked_sources.tooltip')} placement="right">
                  <span>
                    <InformationCircleMiniIcon />
                  </span>
                </Tooltip>
              </TooltipWrapper>
            </HeaderWithTooltip>
          </HeaderCell>
          <HeaderCell columnName="meeting_name">
            {I18n.t('publisher.organization_settings.blocked_sources.host_block.reported_meeting')}
          </HeaderCell>
          <HeaderCell columnName="created_at">
            {I18n.t('publisher.organization_settings.blocked_sources.host_block.block_date')}
          </HeaderCell>
          <HeaderCell>
            {I18n.t('publisher.organization_settings.blocked_sources.host_block.action')}
          </HeaderCell>
        </Header>
        <BlockedPeopleSettingsTableBody blockedPeopleData={data?.results || []} handleBlockAction={handleUnblockModal} />
      </Table>
      <PaginationContainer>
        <Pagination currentPage={page} totalPages={totalPages} totalCount={totalCount} onPageClick={updatePage} pageSize={PAGE_SIZE} />
      </PaginationContainer>
      {isConfirming && selectedBlockedSource && <UnblockConfirmationModal name={selectedBlockedSource.inviteeName} onClose={handleClose} confirm={handleConfirm} />}
    </>;
};

require("./settings.wyw-in-js.css!=!../../../../../node_modules/@wyw-in-js/webpack-loader/lib/outputCssLoader.js?cacheProvider=!./settings.js");